import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { downloadFile } from "@/lib/upload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, FileDown, FileText, Eye, EyeOff } from "lucide-react";
import Pager from "@/components/Pager";

const KATS = [
  { v: "departemen", l: "Departemen" },
  { v: "ribath", l: "Ribath" },
];
const PER_PAGE = 25;

export default function EvaluasiRekap() {
  const [kategori, setKategori] = useState("departemen");
  const [rib, setRib] = useState("all");
  const [dep, setDep] = useState("all");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const qc = useQueryClient();
  const [showExcluded, setShowExcluded] = useState(false);
  const params = { kategori, ribath: rib === "all" ? "" : rib, departemen: dep === "all" ? "" : dep, q: q || "", include_excluded: showExcluded ? 1 : undefined };
  const { data } = useQuery({ queryKey: ["evaluasi-rekap", params], queryFn: async () => (await api.get("/evaluasi/rekap", { params })).data, keepPreviousData: true });
  const { data: options = {} } = useQuery({ queryKey: ["options"], queryFn: async () => (await api.get("/options")).data });
  const exMut = useMutation({
    mutationFn: async ({ id, excluded }) => api.post(`/evaluasi/exclude/${id}`, { excluded }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["evaluasi-rekap"] }); qc.invalidateQueries({ queryKey: ["evaluasi"] }); toast.success("Data evaluasi diperbarui"); },
    onError: () => toast.error("Gagal memperbarui"),
  });
  const toggleExclude = (row) => {
    const to = !row.excluded;
    if (!window.confirm(to ? `Kecualikan ${row.nama} dari rekap evaluasi? Data tetap aman & bisa dimunculkan lagi.` : `Munculkan kembali ${row.nama} ke rekap evaluasi?`)) return;
    exMut.mutate({ id: row.id, excluded: to });
  };

  const rows = data?.rows || [];
  const months = data?.months || [];
  const ambang = data?.ambang ?? 75;

  useEffect(() => { setPage(1); }, [kategori, rib, dep, q]);

  const cols = useMemo(() => {
    const c = [];
    months.forEach((m) => {
      c.push({ type: "bulan", key: m.key, label: m.label });
      if (m.label === "Sep") c.push({ type: "tw", field: "tw1", label: "TW I" });
      if (m.label === "Des") c.push({ type: "tw", field: "tw2", label: "TW II" });
      if (m.label === "Mar") c.push({ type: "tw", field: "tw3", label: "TW III" });
    });
    c.push({ type: "kelulusan", field: "kelulusan", label: "Kelulusan" });
    return c;
  }, [months]);

  const totalPages = Math.max(1, Math.ceil(rows.length / PER_PAGE));
  const pageRows = useMemo(() => rows.slice((page - 1) * PER_PAGE, page * PER_PAGE), [rows, page]);

  // Ringkasan rata-rata per departemen (dari seluruh baris, bukan hanya halaman aktif)
  const summary = useMemo(() => {
    const map = {};
    rows.forEach((r) => {
      const key = (kategori === "ribath" ? r.ribath : r.departemen) || "—";
      if (!map[key]) map[key] = { dep: key, n: 0, tw1: [], tw2: [], tw3: [], kel: [], lulus: 0 };
      const m = map[key];
      m.n++;
      if (r.tw1 != null) m.tw1.push(r.tw1);
      if (r.tw2 != null) m.tw2.push(r.tw2);
      if (r.tw3 != null) m.tw3.push(r.tw3);
      if (r.kelulusan != null) { m.kel.push(r.kelulusan); if (r.kelulusan >= ambang) m.lulus++; }
    });
    const avg = (a) => (a.length ? Math.round(a.reduce((x, y) => x + y, 0) / a.length) : null);
    return Object.values(map)
      .map((m) => ({ dep: m.dep, n: m.n, tw1: avg(m.tw1), tw2: avg(m.tw2), tw3: avg(m.tw3), kel: avg(m.kel), lulus: m.lulus }))
      .sort((a, b) => (b.kel ?? -1) - (a.kel ?? -1));
  }, [rows, ambang, kategori]);

  const val = (row, col) => col.type === "bulan" ? row.bulan?.[col.key] : row[col.field];
  const fmt = (v) => (v == null ? "–" : `${v}%`);
  const cellCls = (col, v) => {
    const below = v != null && v < ambang;
    if (col.type === "tw") return below ? "bg-rose-100 dark:bg-rose-950/50 font-bold text-rose-700 dark:text-rose-300 ring-1 ring-inset ring-rose-300 dark:ring-rose-800" : "bg-rose-50 dark:bg-rose-950/40 font-bold text-rose-700 dark:text-rose-300";
    if (col.type === "kelulusan") return below ? "bg-rose-600 text-white font-extrabold" : "bg-emerald-50 dark:bg-emerald-950/40 font-extrabold text-emerald-800 dark:text-emerald-300";
    if (below) return "text-rose-600 dark:text-rose-400 font-semibold";
    return "text-slate-600 dark:text-slate-300";
  };
  const headCls = (col) => col.type === "tw" ? "bg-rose-600 text-white" : col.type === "kelulusan" ? "bg-emerald-700 text-white" : "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300";
  const lulus = (r) => r.kelulusan != null && r.kelulusan >= ambang;

  const belowKelulusan = rows.filter((r) => r.kelulusan != null && r.kelulusan < ambang).length;

  const exp = (fmtType) => downloadFile(
    `/evaluasi/rekap/export?kategori=${kategori}&format=${fmtType}&ribath=${encodeURIComponent(params.ribath)}&departemen=${encodeURIComponent(params.departemen)}&q=${encodeURIComponent(params.q)}`,
    `Rekap_Keaktifan_${kategori === "ribath" ? "Ribath" : "Departemen"}.${fmtType === "excel" ? "xlsx" : "pdf"}`);

  return (
    <div className="space-y-4" data-testid="evaluasi-rekap">
      {/* Kategori toggle + ekspor */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2">
        <div className="inline-flex rounded-xl bg-emerald-100/60 dark:bg-white/5 p-1 w-full sm:w-auto" data-testid="rekap-kategori-toggle">
          {KATS.map((k) => (
            <button key={k.v} data-testid={`rekap-kat-${k.v}`} onClick={() => setKategori(k.v)}
              className={`flex-1 sm:flex-none px-4 py-1.5 text-sm font-semibold rounded-lg transition-all ${kategori === k.v ? "bg-emerald-700 text-white shadow" : "text-emerald-800 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-white/5"}`}>
              {k.l}
            </button>
          ))}
        </div>
        {isSuper && (
          <button type="button" onClick={() => setShowExcluded((v) => !v)} data-testid="rekap-show-excluded"
            className={`flex items-center justify-center gap-1.5 text-xs font-medium rounded-xl px-3 py-2 border transition-colors w-full sm:w-auto ${showExcluded ? "bg-emerald-600 text-white border-emerald-600" : "bg-white/60 dark:bg-white/5 text-slate-600 dark:text-slate-300 border-slate-200"}`}>
            {showExcluded ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />} Tampilkan yang dikecualikan
          </button>
        )}
        <div className="grid grid-cols-2 gap-2 w-full sm:w-auto sm:ml-auto sm:flex">
          <Button data-testid="rekap-export-excel" onClick={() => exp("excel")} variant="outline" className="rounded-xl bg-white/60 dark:bg-white/5 gap-2 h-9 w-full sm:w-auto"><FileDown className="h-4 w-4" /> Excel</Button>
          <Button data-testid="rekap-export-pdf" onClick={() => exp("pdf")} variant="outline" className="rounded-xl bg-white/60 dark:bg-white/5 gap-2 h-9 w-full sm:w-auto"><FileText className="h-4 w-4" /> PDF</Button>
        </div>
      </div>
      <p className="text-xs text-slate-400">TW = rata-rata 3 bulan · Kelulusan = rata-rata 12 bulan · ambang {ambang}% · <span className="text-rose-600 font-semibold">{belowKelulusan}</span> santri belum lulus</p>

      {/* Filter + paginasi (pojok kanan atas) */}
      <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:gap-3 sm:items-center">
        <div className="relative col-span-2 sm:col-auto"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-500" />
          <Input data-testid="rekap-search" placeholder="Cari nama / no induk..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 w-full sm:w-56 rounded-xl bg-white/60 dark:bg-white/5" /></div>
        <Select value={rib} onValueChange={setRib}><SelectTrigger className="w-full sm:w-40 rounded-xl bg-white/60 dark:bg-white/5" data-testid="rekap-ribath-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua Ribath</SelectItem>{(options.ribath || []).map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select>
        <Select value={dep} onValueChange={setDep}><SelectTrigger className="w-full sm:w-44 rounded-xl bg-white/60 dark:bg-white/5" data-testid="rekap-departemen-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua Departemen</SelectItem>{(options.departemen || []).map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select>
        <div className="col-span-2 sm:ml-auto sm:col-auto flex justify-center sm:justify-end"><Pager page={page} pages={totalPages} total={rows.length} onChange={setPage} testId="rekap-pager" /></div>
      </div>

      {/* Kartu ringkas (mobile) — tanpa geser */}
      <div className="sm:hidden space-y-3" data-testid="rekap-cards">
        {rows.length === 0 && <p className="text-center text-slate-400 py-8">Tidak ada santri untuk filter ini.</p>}
        {pageRows.map((row, ri) => (
          <div key={row.induk || ri} className={`glass rounded-2xl p-4 ${row.excluded ? "opacity-60 ring-1 ring-rose-200" : ""}`} data-testid={`rekap-card-${row.induk}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-bold text-emerald-950 dark:text-emerald-50 break-words leading-tight">{row.no ? `${row.no}. ` : ""}{row.nama}</p>
                <p className="text-[11px] text-slate-500 dark:text-slate-300 mt-0.5 break-words">{row.induk} · {row.departemen || "-"} · {row.ribath || "-"}</p>
              </div>
              <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold ${lulus(row) ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300" : "bg-rose-100 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300"}`}>{lulus(row) ? "LULUS" : "BELUM LULUS"}</span>
            </div>
            <div className="grid grid-cols-4 gap-2 mt-3 text-center">
              {[["TW I", row.tw1], ["TW II", row.tw2], ["TW III", row.tw3]].map(([l, v]) => (
                <div key={l} className="rounded-lg bg-rose-50 dark:bg-rose-950/40 py-1.5">
                  <p className="text-[9px] text-rose-400 font-semibold">{l}</p>
                  <p className={`text-sm font-bold ${v != null && v < ambang ? "text-rose-600 dark:text-rose-400" : "text-rose-700 dark:text-rose-300"}`}>{fmt(v)}</p>
                </div>
              ))}
              <div className={`rounded-lg py-1.5 ${lulus(row) ? "bg-emerald-50 dark:bg-emerald-950/40" : "bg-rose-600"}`}>
                <p className={`text-[9px] font-semibold ${lulus(row) ? "text-emerald-500" : "text-rose-100"}`}>Lulus</p>
                <p className={`text-sm font-extrabold ${lulus(row) ? "text-emerald-800 dark:text-emerald-300" : "text-white"}`}>{fmt(row.kelulusan)}</p>
              </div>
            </div>
            <details className="mt-2">
              <summary className="text-[11px] text-emerald-700 cursor-pointer select-none">Rincian bulanan</summary>
              <div className="grid grid-cols-4 gap-1.5 mt-2">
                {months.map((m) => { const v = row.bulan?.[m.key]; return (
                  <div key={m.key} className="text-center rounded bg-white/60 dark:bg-white/5 py-1">
                    <p className="text-[8px] text-slate-400 uppercase">{m.label}</p>
                    <p className={`text-[11px] font-semibold ${v != null && v < ambang ? "text-rose-500" : "text-slate-600 dark:text-slate-300"}`}>{v == null ? "–" : `${v}%`}</p>
                  </div>
                ); })}
              </div>
            </details>
            {isSuper && (
              <button data-testid={`rekap-exclude-${row.induk}`} onClick={() => toggleExclude(row)}
                className={`mt-2 w-full text-xs font-medium rounded-lg py-1.5 ${row.excluded ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300" : "bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400"}`}>
                {row.excluded ? "Munculkan ke evaluasi" : "Kecualikan dari evaluasi"}
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Tabel rekap (desktop / tablet) */}
      <div className="hidden sm:block glass rounded-2xl p-2 sm:p-3 overflow-x-auto" data-testid="rekap-table">
        <table className="text-xs border-collapse min-w-max">
          <thead>
            <tr>
              <th className="sm:sticky sm:left-0 sm:z-10 bg-emerald-700 text-white px-2 py-2 text-left rounded-l-lg">No</th>
              <th className="sm:sticky sm:left-8 sm:z-10 bg-emerald-700 text-white px-2 py-2 text-left min-w-[160px]">Nama</th>
              <th className="bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 px-2 py-2 text-left">Departemen</th>
              <th className="bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 px-2 py-2 text-left">Ribath</th>
              <th className="bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 px-2 py-2 text-left">Prodi</th>
              <th className="bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 px-2 py-2 text-left">Status</th>
              {cols.map((c, i) => (
                <th key={i} className={`px-2 py-2 text-center whitespace-nowrap ${headCls(c)}`}>{c.label}</th>
              ))}
              <th className="bg-slate-800 text-white px-2 py-2 text-center whitespace-nowrap">Status</th>
              {isSuper && <th className="bg-slate-800 text-white px-2 py-2 text-center whitespace-nowrap">Aksi</th>}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={7 + cols.length} className="text-center text-slate-400 py-8">Tidak ada santri untuk filter ini.</td></tr>
            )}
            {pageRows.map((row, ri) => (
              <tr key={row.induk || ri} className={`border-b border-slate-100 dark:border-white/10 hover:bg-emerald-50/40 dark:hover:bg-white/5 ${row.excluded ? "opacity-60" : ""}`} data-testid={`rekap-row-${row.induk}`}>
                <td className="sm:sticky sm:left-0 sm:z-10 bg-white dark:bg-[#202620] px-2 py-1.5 text-slate-400 dark:text-slate-500">{row.no || (page - 1) * PER_PAGE + ri + 1}</td>
                <td className="sm:sticky sm:left-8 sm:z-10 bg-white dark:bg-[#202620] px-2 py-1.5 font-semibold text-emerald-950 dark:text-emerald-50 whitespace-nowrap">{row.nama}</td>
                <td className="px-2 py-1.5 text-slate-500 dark:text-slate-300 whitespace-nowrap">{row.departemen || "-"}</td>
                <td className="px-2 py-1.5 text-slate-500 dark:text-slate-300 whitespace-nowrap">{row.ribath || "-"}</td>
                <td className="px-2 py-1.5 text-slate-500 dark:text-slate-300 whitespace-nowrap">{row.prodi || "-"}</td>
                <td className="px-2 py-1.5 text-slate-500 dark:text-slate-300 whitespace-nowrap">{row.status || "-"}</td>
                {cols.map((c, ci) => {
                  const v = val(row, c);
                  return <td key={ci} className={`px-2 py-1.5 text-center whitespace-nowrap ${cellCls(c, v)}`} data-testid={`rekap-${c.field || c.key}-${row.induk}`}>{fmt(v)}</td>;
                })}
                <td className="px-2 py-1.5 text-center whitespace-nowrap" data-testid={`rekap-kelulusan-status-${row.induk}`}>
                  <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${lulus(row) ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300" : "bg-rose-100 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300"}`}>
                    {lulus(row) ? "LULUS" : "BELUM LULUS"}
                  </span>
                </td>
                {isSuper && (
                  <td className="px-2 py-1.5 text-center whitespace-nowrap">
                    <button data-testid={`rekap-exclude-row-${row.induk}`} onClick={() => toggleExclude(row)} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${row.excluded ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300" : "bg-rose-100 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300"}`}>{row.excluded ? "Munculkan" : "Kecualikan"}</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Ringkasan rata-rata per Departemen */}
      {summary.length > 0 && (
        <div className="glass rounded-2xl p-3 sm:p-4 overflow-x-auto" data-testid="rekap-summary">
          <h4 className="font-bold text-emerald-900 dark:text-emerald-100 mb-3 text-sm">Ringkasan Rata-rata per {kategori === "ribath" ? "Ribath" : "Departemen"} <span className="font-normal text-slate-400">(urut dari paling aktif)</span></h4>
          <table className="text-xs border-collapse min-w-[560px] w-full">
            <thead>
              <tr className="text-left text-slate-500 dark:text-slate-300 border-b border-emerald-100 dark:border-white/10">
                <th className="py-2 px-2 font-semibold">{kategori === "ribath" ? "Ribath" : "Departemen"}</th>
                <th className="py-2 px-2 font-semibold text-center">Santri</th>
                <th className="py-2 px-2 font-semibold text-center">TW I</th>
                <th className="py-2 px-2 font-semibold text-center">TW II</th>
                <th className="py-2 px-2 font-semibold text-center">TW III</th>
                <th className="py-2 px-2 font-semibold text-center bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300">Kelulusan</th>
                <th className="py-2 px-2 font-semibold text-center">Lulus</th>
              </tr>
            </thead>
            <tbody>
              {summary.map((s) => (
                <tr key={s.dep} className="border-b border-slate-100 dark:border-white/10" data-testid={`rekap-summary-${s.dep}`}>
                  <td className="py-2 px-2 font-semibold text-emerald-950 dark:text-emerald-50 whitespace-nowrap">{s.dep}</td>
                  <td className="py-2 px-2 text-center text-slate-500 dark:text-slate-300">{s.n}</td>
                  <td className={`py-2 px-2 text-center font-bold ${s.tw1 != null && s.tw1 < ambang ? "text-rose-600" : "text-slate-700 dark:text-slate-200"}`}>{fmt(s.tw1)}</td>
                  <td className={`py-2 px-2 text-center font-bold ${s.tw2 != null && s.tw2 < ambang ? "text-rose-600" : "text-slate-700 dark:text-slate-200"}`}>{fmt(s.tw2)}</td>
                  <td className={`py-2 px-2 text-center font-bold ${s.tw3 != null && s.tw3 < ambang ? "text-rose-600" : "text-slate-700 dark:text-slate-200"}`}>{fmt(s.tw3)}</td>
                  <td className={`py-2 px-2 text-center font-extrabold ${s.kel != null && s.kel < ambang ? "bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400" : "bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300"}`}>{fmt(s.kel)}</td>
                  <td className="py-2 px-2 text-center text-slate-600 dark:text-slate-300">{s.lulus}/{s.n}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
